// =============================================================================
// UserStatusContext — Global presence status for ALL org members
// Computes: focus → 'dnd', absence → 'absent', else → 'available'
// =============================================================================

import React, { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useOrganization } from './OrganizationContext';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { UserStatus } from '@/components/ui/status-avatar';
import {
  fetchOrgStatusEvents,
  fetchStatusParticipants,
  fetchOrgMemberIds,
  type StatusEvent,
} from '@/modules/planning/data/statusRepository';

interface UserStatusContextType {
  /** Get status for a specific org member ID */
  getStatus: (orgMemberId: string) => UserStatus;
  /** Current user's org member ID (resolved once) */
  currentMemberId: string | null;
  /** Force recompute */
  refresh: () => void;
}

const UserStatusContext = createContext<UserStatusContextType>({
  getStatus: () => 'available',
  currentMemberId: null,
  refresh: () => {},
});

export function useUserStatusContext() {
  return useContext(UserStatusContext);
}

function isEventActiveNow(startAt: string | null, endAt: string, now: Date): boolean {
  const parse = (s: string | null) => {
    if (!s) return null;
    const d = new Date(s.replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const end = parse(endAt);
  if (!end || Number.isNaN(end.getTime())) return false;
  const endOfMinute = new Date(end.getTime() + 59999);

  const start = parse(startAt);
  if (!start || Number.isNaN(start.getTime())) return now <= endOfMinute;
  return start <= now && now <= endOfMinute;
}

export function UserStatusProvider({ children }: { children: React.ReactNode }) {
  // Batch 3 (B3.3): polling reduced from 5s to 30s. Realtime on
  // planning_events + planning_event_participants and window focus /
  // visibilitychange remain the primary signals; this interval is a fallback.
  const STATUS_SYNC_INTERVAL_MS = 30000;
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const orgId = currentOrganization?.id;

  const [statusMap, setStatusMap] = useState<Map<string, UserStatus>>(new Map());
  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);
  const [onlineMemberIds, setOnlineMemberIds] = useState<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const computeInFlightRef = useRef(false);

  // Resolve current user's org member ID
  useEffect(() => {
    if (!orgId || !user?.id) { setCurrentMemberId(null); return; }

    (async () => {
      const { data } = await supabase
        .from('core_organization_members')
        .select('id')
        .eq('org_id', orgId)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();
      setCurrentMemberId(data?.id || null);
    })();
  }, [orgId, user?.id]);

  const compute = useCallback(async () => {
    if (!orgId || computeInFlightRef.current) return;

    computeInFlightRef.current = true;

    try {
      const events = await fetchOrgStatusEvents(orgId);
      const now = new Date();

      // Filter to only currently active events
      const activeEvents = events.filter(e => isEventActiveNow(e.start_at, e.end_at, now));

      if (activeEvents.length === 0) {
        setStatusMap(new Map());
        scheduleNextTransition(events, now);
        return;
      }

      // Fetch participants for active events
      const participants = await fetchStatusParticipants(orgId, activeEvents.map(e => e.id));

      // Build a map: memberId -> best status
      const map = new Map<string, UserStatus>();

      for (const evt of activeEvents) {
        const status: UserStatus = evt.event_type === 'focus' ? 'dnd' : 'absent';
        applyStatus(map, evt.created_by, status);
        for (const p of participants) {
          if (p.event_id === evt.id) {
            applyStatus(map, p.org_member_id, status);
          }
        }
      }

      setStatusMap(map);
      scheduleNextTransition(events, now);
    } finally {
      computeInFlightRef.current = false;
    }
  }, [orgId]);

  function applyStatus(map: Map<string, UserStatus>, memberId: string, status: UserStatus) {
    const current = map.get(memberId);
    if (current === 'dnd') return;
    if (status === 'dnd' || !current) {
      map.set(memberId, status);
    }
  }

  function scheduleNextTransition(events: StatusEvent[], now: Date) {
    if (timerRef.current) clearTimeout(timerRef.current);

    const parse = (s: string | null) => {
      if (!s) return null;
      const d = new Date(s.replace(' ', 'T'));
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const futurePoints: number[] = [];
    for (const evt of events) {
      const start = parse(evt.start_at);
      const end = parse(evt.end_at);
      if (start && start.getTime() > now.getTime()) futurePoints.push(start.getTime());
      if (end) {
        const endTransition = end.getTime() + 60000;
        if (endTransition > now.getTime()) futurePoints.push(endTransition);
      }
    }

    if (futurePoints.length > 0) {
      const next = Math.min(...futurePoints);
      const delay = next - now.getTime() + 1000;
      if (delay > 0 && delay < 86400000) {
        timerRef.current = setTimeout(compute, delay);
      }
    }
  }

  useEffect(() => {
    if (!orgId) {
      setStatusMap(new Map());
      return;
    }

    compute();
    intervalRef.current = setInterval(() => {
      compute();
    }, STATUS_SYNC_INTERVAL_MS);

    const onPlanningChanged = () => compute();
    const onWindowFocus = () => compute();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') compute();
    };
    window.addEventListener('planning:events-changed', onPlanningChanged);
    window.addEventListener('focus', onWindowFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Realtime + short polling fallback: private status events may not always
    // emit a usable cross-user signal, so polling keeps avatars in sync without refresh.
    const channel = supabase
      .channel(`global-user-status-${orgId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'planning_events',
          filter: `org_id=eq.${orgId}`,
        },
        () => compute(),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'planning_event_participants',
        },
        () => compute(),
      )
      .subscribe();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('planning:events-changed', onPlanningChanged);
      window.removeEventListener('focus', onWindowFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      supabase.removeChannel(channel);
    };
  }, [orgId, compute, STATUS_SYNC_INTERVAL_MS]);

  // ---------------------------------------------------------------------------
  // Online presence — tracks who is currently connected to this org via a
  // shared Supabase Realtime presence channel. A member missing from the
  // presence state is reported as 'offline' (overrides 'available' but lets
  // 'dnd' / 'absent' planning statuses take precedence so scheduled focus or
  // absence still shows even if the user is logged out).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!orgId || !currentMemberId) {
      setOnlineMemberIds(new Set());
      return;
    }

    const channel = supabase.channel(`org-online:${orgId}`, {
      config: { presence: { key: currentMemberId } },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState() as Record<string, unknown[]>;
        setOnlineMemberIds(new Set(Object.keys(state)));
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({ memberId: currentMemberId, at: Date.now() });
        }
      });

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void channel.track({ memberId: currentMemberId, at: Date.now() });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      void channel.untrack();
      supabase.removeChannel(channel);
    };
  }, [orgId, currentMemberId]);

  const getStatus = useCallback(
    (orgMemberId: string): UserStatus => {
      const planning = statusMap.get(orgMemberId);
      // dnd (focus) and absent are scheduled — show them even if offline.
      if (planning === 'dnd' || planning === 'absent') return planning;
      // Otherwise: connected → available, disconnected → offline.
      return onlineMemberIds.has(orgMemberId) ? (planning || 'available') : 'offline';
    },
    [statusMap, onlineMemberIds],
  );

  // Batch 5A (Q3): memoize context value to keep referential identity stable
  // across renders that don't change status data.
  const value = useMemo(
    () => ({ getStatus, currentMemberId, refresh: compute }),
    [getStatus, currentMemberId, compute],
  );

  return (
    <UserStatusContext.Provider value={value}>
      {children}
    </UserStatusContext.Provider>
  );
}
